import { Injectable } from '@angular/core';
import { isNil, reject } from 'lodash-es';

import { UserModulePushOutput } from 'infrastructure/graphql/messaging/user/model/user-module-push.output';
import { UserAvailabilityType } from 'user/model/user-availability.type';
import { UserRole } from 'user/model/user-role';
import { UserStatus } from 'user/model/user-status';
import { UserOutput } from 'user/model/user.output';
import { UserPartner } from 'user/partner/user-partner';
import { User } from 'user/user';
import { UserAvailability } from 'user/user-availability';
import { UserMetadata } from 'user/user-metadata';
import { UserModulePushConverterService } from 'user/user-module-push-converter.service';
import { UserReference } from 'user/user-reference';

import { AppLocator } from '../../app.locator';

@Injectable({ providedIn: 'root' })
export class UserConverterService {
	private get _userModulePushConverterService(): UserModulePushConverterService {
		return AppLocator.inject<UserModulePushConverterService>(
			UserModulePushConverterService
		);
	}

	public userAsEntity(
		output: UserOutput,
		modulePushesOutput: UserModulePushOutput[] = []
	): User {
		if (!output) {
			return null;
		}
		return new User(
			output.id,
			UserRole[output.role],
			output.hasMfa,
			output.coach?.id,
			output.timeZone,
			output.metadata?.isGhost,
			output.lastName,
			output.firstName,
			UserStatus[output.status],
			output.avatarUrl,
			output.emailAddress,
			output.phoneNumber,
			output.partnersId || [],
			output.intercomHash,
			output.observersId || [],
			output.organization?.id,
			output.organization?.sectors,
			!!output.metadata
				? new UserMetadata(
						output.metadata?.moodFrequency,
						output.metadata?.moodDescriptionShown,
						output.metadata?.registrationTimestamp,
						output.metadata?.currentAddress,
						output.metadata?.birthAddress,
						output.metadata?.linkedInUrl,
						output.metadata?.language,
						new Date(output.metadata?.birthDate),
						null,
						output.metadata?.quote
				  )
				: null,
			output.creationTimestamp,
			output.updateTimestamp,
			output.partners?.map(
				(p) =>
					({
						organizationId: p.organizationId,
						rights: p.partnerRights.map((r) => ({
							target: r.target,
							access: r.access,
						})),
					} as UserPartner)
			),
			(output.references || [])
				.filter((c) => c.type === 'DIPLOMA')
				.map(
					(diploma) =>
						new UserReference(
							diploma.id,
							diploma.name,
							new Date(diploma.endDate),
							new Date(diploma.startDate),
							diploma.institution
						)
				),
			(output.references || [])
				.filter((c) => c.type === 'CERTIFICATE')
				.map(
					(certificate) =>
						new UserReference(
							certificate.id,
							certificate.name,
							new Date(certificate.endDate),
							new Date(certificate.startDate),
							certificate.institution
						)
				),
			(output.availabilities || []).map(
				(availability) =>
					new UserAvailability(
						availability.dayIndex,
						availability.endOffset,
						availability.startOffset,
						UserAvailabilityType[availability.type],
						availability.id
					)
			),
			this._userModulePushConverterService.userModulePushesAsEntities(
				modulePushesOutput
			)
		);
	}

	public usersAsEntities(outputs: UserOutput[]): User[] {
		return reject(outputs, isNil).map((output) => this.userAsEntity(output));
	}
}
